/**
 * HotspotStatusChips — Story 1.6 AC-1
 *
 * Hero-Status-Chips für CDR-Verifikation und Ban-Status auf Basis der
 * additiven Detail-Felder (cdr_verification, ban_status). Nutzt die
 * StatusChip-Komponente aus components/ui (Ein-Satz-Tooltips inklusive,
 * Restdauer im Problemfall via `remaining`).
 *
 * Mapping API → StatusChip:
 *  - cdr_verification.status 'verified' → cdr/valid, 'failed' → cdr/failed.
 *    'unknown'/'unavailable' (dokumentierte Datenlücke: der
 *    CdrVerificationAdapter hat noch keine Datenquelle) → neutraler Chip mit
 *    eigenem Tooltip statt eines falsch-grünen/roten Zustands.
 *  - ban_status: banned ODER denylist_listed → ban/active (Restdauer aus
 *    estimated_remaining_days, sofern das BE sie liefert — laut Contract
 *    derzeit immer null, der Chip zeigt dann nur „Gebannt"). Sonst ban/clean.
 */
import { useTranslation } from 'react-i18next'
import { HelpCircle } from 'lucide-react'
import { StatusChip, Tooltip } from '../../components/ui'
import type { HotspotDetail } from './useHotspotDetail'

type Props = {
  hotspot: Pick<HotspotDetail, 'cdr_verification' | 'ban_status'>
}

export function HotspotStatusChips({ hotspot }: Props) {
  const { t } = useTranslation('hotspotDetailTabs')
  const cdr = hotspot.cdr_verification
  const ban = hotspot.ban_status

  const banned = Boolean(ban?.banned || ban?.state === 'banned' || ban?.denylist_listed)
  const remainingDays = ban?.estimated_remaining_days
  const remaining =
    banned && remainingDays != null && remainingDays > 0
      ? { days: remainingDays }
      : null

  return (
    <>
      {cdr &&
        (cdr.status === 'verified' || cdr.status === 'failed' ? (
          <StatusChip
            kind="cdr"
            status={cdr.status === 'verified' ? 'valid' : 'failed'}
            data-testid="status-chip-cdr"
          />
        ) : (
          <Tooltip text={t('hero.cdrUnavailable.tooltip')}>
            {(tipId) => (
              <span
                className="pill ui-status-chip"
                data-testid="status-chip-cdr"
                data-status={cdr.status ?? 'unavailable'}
                data-tone="neutral"
                tabIndex={0}
                aria-describedby={tipId}
              >
                <HelpCircle aria-hidden="true" size={13} className="ui-status-chip__icon" />
                {t('hero.cdrUnavailable.label')}
              </span>
            )}
          </Tooltip>
        ))}

      {ban && (
        <StatusChip
          kind="ban"
          status={banned ? 'active' : 'clean'}
          remaining={remaining}
          data-testid="status-chip-ban"
        />
      )}
    </>
  )
}
